import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { createEnquiry } from "@/lib/api";

const REVENUE_BANDS = [
  "Under A$250k",
  "A$250k – A$1M",
  "A$1M – A$3M",
  "A$3M – A$10M",
  "Over A$10M",
];

/**
 * Retainer enquiry.
 *
 * Replaces the previous `mailto:hello@margindesk.io` CTA. That address sat on a
 * domain that is not owned, and mailto silently does nothing for the large share
 * of desktop users with no mail client configured — on the highest-value CTA on
 * the page.
 */
export default function EnquiryForm({ open, onOpenChange }) {
  const [form, setForm] = useState({
    name: "", email: "", store_domain: "", revenue_band: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Your name is required");
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return toast.error("Enter a valid email");
    if (!accepted) return toast.error("Please accept the terms before sending");
    try {
      setBusy(true);
      await createEnquiry({ ...form, topic: "retainer", accept_terms: true });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not send that. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setSent(false); setAccepted(false); }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="enquiry-dialog"
        className="bg-[#f5f2ed] border border-[#c9c3b8] text-[#171717] max-w-lg p-0 max-h-[90vh] overflow-y-auto"
      >
        <div className="p-8">
          {sent ? (
            <div data-testid="enquiry-sent">
              <div className="tick mb-3 flex items-center gap-2 text-[#9a3412]">
                <Check size={12} /> Received
              </div>
              <DialogTitle className="font-serif-display text-3xl leading-tight text-[#171717] mb-4">
                It's with the analyst.
              </DialogTitle>
              <p className="font-mono-tech text-[13px] text-[#525252] leading-relaxed mb-8">
                Your enquiry is logged. The retainer is a conversation, not a
                checkout — someone will read this and come back to you directly.
              </p>
              <button className="btn-primary" onClick={() => close(false)} data-testid="enquiry-close">
                Close
              </button>
            </div>
          ) : (
            <>
              <DialogHeader className="text-left space-y-2 mb-6">
                <div className="tick">Monthly Retainer · A$4,500</div>
                <DialogTitle className="font-serif-display text-3xl leading-tight text-[#171717]">
                  Get in touch.
                </DialogTitle>
                <DialogDescription className="font-mono-tech text-[12px] text-[#525252] leading-relaxed">
                  The retainer is your commercial intelligence function on a monthly cycle, so it
                  starts with a conversation rather than a card. Tell us where you are.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="tick block mb-2" htmlFor="enq-name">Name</label>
                    <input id="enq-name" data-testid="enquiry-name" className="input-vault"
                           value={form.name} onChange={set("name")} autoComplete="name" />
                  </div>
                  <div>
                    <label className="tick block mb-2" htmlFor="enq-email">Email</label>
                    <input id="enq-email" data-testid="enquiry-email" type="email" className="input-vault"
                           placeholder="you@yourbrand.com" value={form.email} onChange={set("email")} />
                  </div>
                </div>

                <div>
                  <label className="tick block mb-2" htmlFor="enq-domain">Store domain</label>
                  <input id="enq-domain" data-testid="enquiry-domain" className="input-vault"
                         placeholder="yourbrand.com" value={form.store_domain}
                         onChange={set("store_domain")} spellCheck={false} />
                </div>

                <div>
                  <label className="tick block mb-2" htmlFor="enq-band">Annual revenue</label>
                  <select id="enq-band" data-testid="enquiry-band" className="input-vault"
                          value={form.revenue_band} onChange={set("revenue_band")}>
                    <option value="">Prefer not to say</option>
                    {REVENUE_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>

                <div>
                  <label className="tick block mb-2" htmlFor="enq-msg">
                    What's the pricing question?
                  </label>
                  <textarea id="enq-msg" data-testid="enquiry-message" rows={4}
                            className="input-vault resize-y"
                            placeholder="Where margin is leaking, what you've already tried, what a decision would unblock."
                            value={form.message} onChange={set("message")} />
                </div>

                <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    data-testid="enquiry-accept-terms"
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[#e88a51] cursor-pointer"
                  />
                  <span className="font-mono-tech text-[11px] text-[#525252] leading-relaxed">
                    I agree to the{" "}
                    <a href="/legal/terms" target="_blank" rel="noopener noreferrer"
                       className="text-[#9a3412] underline underline-offset-2">Terms of Service</a>{" "}
                    and{" "}
                    <a href="/legal/privacy" target="_blank" rel="noopener noreferrer"
                       className="text-[#9a3412] underline underline-offset-2">Privacy Policy</a>.
                    An enquiry is not a contract - any engagement is governed by the{" "}
                    <a href="/legal/services-agreement" target="_blank" rel="noopener noreferrer"
                       className="text-[#9a3412] underline underline-offset-2">Services Agreement</a>,
                    which you accept before work starts.
                  </span>
                </label>

                <div className="flex items-center justify-between pt-2 gap-4">
                  <p className="font-mono-tech text-[11px] text-[#525252]">
                    Goes straight to the analyst. Not a mailing list.
                  </p>
                  <button type="submit" disabled={busy || !accepted} data-testid="enquiry-submit"
                          className="btn-primary disabled:opacity-60 shrink-0">
                    {busy ? "Sending…" : (<>Send <ArrowRight size={14} /></>)}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
